import React from "react";
import RegisterPage, { RATING_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiFetch } from "../lib/api";

export default function ContractorPerformance() {
  const { data, siteNames } = useHSE();
  const rows = data.contractorPerformance || data.contractors || [];

  const handleSaveRecord = async (editingRecord) => {
    try {
      const res = await apiFetch("/contractor-assurance", {
        method: "POST",
        body: {
          contractorName: editingRecord.name || editingRecord.contractorName,
          licenseNumber: editingRecord.licenseNumber || editingRecord.id || `LIC-${editingRecord.name}`,
          siteId: editingRecord.siteId,
          riskTier: editingRecord.riskTier || "high",
          prequalificationStatus: editingRecord.prequalificationStatus || "approved",
          hsePlanApproval: editingRecord.hsePlanApproval !== false,
          auditScore: Number(editingRecord.auditScore) || 85,
          totalWorkers: Number(editingRecord.totalWorkers) || 50,
          idempotencyKey: `ctr-${editingRecord.id}`,
        },
      });
      return res?.item ? { ...editingRecord, ...res.item } : editingRecord;
    } catch (err) {
      return editingRecord;
    }
  };

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard label="Contractors Registered" value={rows.length} testid="cp-total" />
      <StatCard label="Prequalified & Approved" value={rows.filter((r) => r.prequalificationStatus === "approved" || r.hsePlanApproval).length} tone="safe" testid="cp-approved" />
      <StatCard label="High / Critical Risk" value={rows.filter((r) => r.riskTier === "high" || r.riskTier === "critical").length} tone="warning" testid="cp-risk" />
      <StatCard label="Action / Alert Required" value={rows.filter((r) => r.actionId || r.prequalificationStatus === "rejected").length} tone="critical" testid="cp-capa" />
    </div>
  );

  return (
    <RegisterPage
      title="Contractor HSE Performance & Prequalification"
      subtitle="Prequalification, Risk Tiering, HSE Plan Approvals & Audit Scoring — ISO 45001 §8.1.4"
      storeKey="contractorPerformance"
      idPrefix="CTR"
      topPanels={Top}
      onSaveRecord={handleSaveRecord}
      columns={[
        { key: "id", label: "ID" },
        { key: "contractorName", label: "Contractor Name", render: (r) => <span className="font-semibold">{r.contractorName || r.name}</span> },
        { key: "licenseNumber", label: "License No", render: (r) => <span className="font-mono">{r.licenseNumber || r.id}</span> },
        { key: "riskTier", label: "Risk Tier", render: (r) => <span className={`uppercase text-[10px] font-bold px-2 py-0.5 rounded ${r.riskTier === "critical" ? "bg-rose-100 text-rose-800" : r.riskTier === "high" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{r.riskTier || "high"}</span> },
        { key: "prequalificationStatus", label: "Prequal Status", render: (r) => <span className={`uppercase text-[10px] font-bold px-2 py-0.5 rounded ${r.prequalificationStatus === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{r.prequalificationStatus || "approved"}</span> },
        { key: "hsePlanApproval", label: "HSE Plan", render: (r) => (r.hsePlanApproval || r.hsePlanApproved) ? <span className="text-emerald-700 font-bold">Approved</span> : <span className="text-rose-600 font-bold">Pending</span> },
        { key: "auditScore", label: "Audit Score", render: (r) => <span className="font-mono font-bold">{r.auditScore}%</span> },
        { key: "actionId", label: "Linked CAPA", render: (r) => r.actionId ? <span className="font-mono text-[11px] text-[#18B84F]">{r.actionId}</span> : <span className="text-slate-300">—</span> },
      ]}
      badgeMap={{ rating: RATING_BADGES }}
      formFields={[
        { key: "id", label: "Contractor ID" },
        { key: "contractorName", label: "Contractor Name" },
        { key: "licenseNumber", label: "License Number" },
        { key: "site", label: "Primary Operating Site", type: "select", options: siteNames },
        { key: "riskTier", label: "Risk Tier", type: "select", options: ["low", "medium", "high", "critical"] },
        { key: "prequalificationStatus", label: "Prequalification Status", type: "select", options: ["pending", "approved", "rejected", "suspended"] },
        { key: "hsePlanApproval", label: "HSE Plan Approved?", type: "select", options: [true, false] },
        { key: "auditScore", label: "HSE Audit Score %", type: "number" },
        { key: "totalWorkers", label: "Total Workers Onsite", type: "number" },
      ]}
    />
  );
}
